import axios from 'axios';
import { Debt, DebtDetails } from '../../types';
import { getAuthHeader } from '../authService';
import { API_BASE_URL } from '../../utils/constants';

// Configure axios to include auth headers
const getAxiosConfig = () => ({
  headers: getAuthHeader()
});

// Get all debts
export const getDebts = async (): Promise<Debt[]> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/debts`,
      getAxiosConfig()
    );
    
    // Handle API response format: {success, message, data}
    if (response.data && response.data.data) {
      return Array.isArray(response.data.data) ? response.data.data : [];
    }
    
    // Fallback: if response.data is already an array
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching debts:', error);
    return [];
  }
};

// Get debt by ID with details and installments
export const getDebtById = async (id: string): Promise<DebtDetails | null> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/debts/${id}/details`,
      getAxiosConfig()
    );
    
    // Handle API response format: {success, message, data}
    if (response.data && response.data.data) {
      return response.data.data;
    }
    
    // Fallback: if response.data is the debt details object directly
    return response.data;
  } catch (error) {
    console.error('Error fetching debt by ID:', error);
    return null;
  }
};

// Download monthly template
export const downloadMonthlyTemplate = async (): Promise<Blob> => {
  const response = await axios.get(
    `${API_BASE_URL}/debts/template/download`,
    {
      ...getAxiosConfig(),
      responseType: 'blob',
    }
  );
  return response.data;
};

// Upload monthly update
export const uploadMonthlyUpdate = async (file: File): Promise<void> => {
  const formData = new FormData();
  formData.append('file', file);
  
  await axios.post(
    `${API_BASE_URL}/debts/upload`,
    formData,
    {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'multipart/form-data',
      },
    }
  );
};

type ApiEnvelope<T> = {
  success?: boolean;
  message?: string;
  data?: T;
};

export const increaseInstallmentAmount = async (
  installmentId: string,
  newAmount: number
): Promise<unknown> => {
  const response = await axios.post<ApiEnvelope<unknown>>(
    `${API_BASE_URL}/installments/${installmentId}/increase`,
    { newAmount },
    getAxiosConfig()
  );

  return response.data?.data ?? response.data;
};

export const decreaseInstallmentAmount = async (
  installmentId: string,
  newAmount: number
): Promise<unknown> => {
  const response = await axios.post<ApiEnvelope<unknown>>(
    `${API_BASE_URL}/installments/${installmentId}/decrease`,
    { newAmount },
    getAxiosConfig()
  );

  return response.data?.data ?? response.data;
};

export const fullDebtPayment = async (
  debtId: string,
  paymentAmount: number,
  paymentDate: string
): Promise<unknown> => {
  const response = await axios.post<ApiEnvelope<unknown>>(
    `${API_BASE_URL}/installments/debt/${debtId}/full-payment`,
    { paymentAmount, paymentDate },
    getAxiosConfig()
  );

  return response.data?.data ?? response.data;
};

export const increaseDebtInstallments = async (
  debtId: string,
  newTotalInstallments: number
): Promise<unknown> => {
  const response = await axios.post<ApiEnvelope<unknown>>(
    `${API_BASE_URL}/installments/debt/${debtId}/increase-installments`,
    { newTotalInstallments },
    getAxiosConfig()
  );

  return response.data?.data ?? response.data;
};


